import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  MessageSquarePlus,
  X,
  Star,
  ImagePlus,
  Trash2,
  Plus,
  Search,
  RotateCw,
  ShieldAlert,
  Clock,
  ChevronDown,
  User,
  Phone,
  Mail,
  ArrowLeft,
} from "lucide-react";
import {
  submitAppFeedback,
  getAppFeedbackList,
  updateAppFeedbackStatus,
  deleteAppFeedback,
} from "../../api/system/feedback";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../ConfirmDialog";
import { formatDateTimeVN } from "../../utils/formatUtils";
import packageJson from "../../../package.json";

const CATEGORIES = [
  { value: "Bug", label: "Báo lỗi" },
  { value: "Support", label: "Nhờ hỗ trợ" },
  { value: "Feature", label: "Đề xuất tính năng" },
  { value: "Feedback", label: "Góp ý chung" },
];

const PRIORITIES = [
  { value: "Low", label: "Thấp" },
  { value: "Medium", label: "Trung bình" },
  { value: "High", label: "Cao" },
  { value: "Critical", label: "Khẩn cấp" },
];

const STATUS_CONFIG = {
  pending: {
    label: "Đang chờ xử lý",
    badge: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
  },
  in_progress: {
    label: "Đã tiếp nhận",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
  },
  resolved: {
    label: "Đã xử lý",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
  },
};

const CATEGORY_BADGES = {
  Bug: "bg-red-50 text-red-700 border-red-200",
  Support: "bg-sky-50 text-sky-700 border-sky-200",
  Feature: "bg-purple-50 text-purple-700 border-purple-200",
  Feedback: "bg-gray-100 text-gray-700 border-gray-200",
};

const PRIORITY_BADGES = {
  Low: "text-slate-500",
  Medium: "text-blue-600 font-medium",
  High: "text-amber-600 font-bold",
  Critical: "text-red-600 font-bold",
};

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const inputClass =
  "w-full appearance-none rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm outline-none transition-all focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

function FieldLabel({ children, required }) {
  return (
    <label className="mb-1.5 ml-1 block text-[11px] font-bold uppercase tracking-widest text-gray-400">
      {children}
      {required ? <span className="ml-0.5 text-red-600">*</span> : null}
    </label>
  );
}

function buildDeviceInfo() {
  if (typeof window === "undefined") return null;
  return {
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    currentUrl: window.location.href,
    isPWA: window.matchMedia("(display-mode: standalone)").matches,
    timestamp: new Date().toISOString(),
  };
}

function revokePreviews(items) {
  items.forEach((item) => {
    if (item?.preview) URL.revokeObjectURL(item.preview);
  });
}

export default function FeedbackModal({ open, onClose }) {
  // Giao diện mở ra xem danh sách trước (giống hub thông báo)
  const [view, setView] = useState("list"); // "list" | "create"

  // Form State
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("Bug");
  const [priority, setPriority] = useState("Medium");
  const [rating, setRating] = useState(0);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // History State
  const [tickets, setTickets] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const resetForm = () => {
    setImages((prev) => {
      revokePreviews(prev);
      return [];
    });
    setTitle("");
    setMessage("");
    setCategory("Bug");
    setPriority("Medium");
    setRating(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    return () => revokePreviews(images);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tải danh sách nhật ký
  const loadHistory = useCallback(
    async (targetPage = 1, status = statusFilter, keyword = searchKeyword) => {
      setLoadingHistory(true);
      try {
        const res = await getAppFeedbackList({
          status: status || undefined,
          keyword: keyword || undefined,
          page: targetPage,
          limit: 10,
        });
        setTickets(Array.isArray(res.data) ? res.data : []);
        setTotal(Number(res.total || 0));
        setTotalPages(Number(res.totalPages || 1));
        setPage(Number(res.page || 1));
        setIsSuperadmin(Boolean(res.is_superadmin));
      } catch (err) {
        console.error("Lỗi tải nhật ký hỗ trợ:", err);
        showErrorToast("Không tải được danh sách nhật ký hỗ trợ.");
      } finally {
        setLoadingHistory(false);
      }
    },
    [statusFilter, searchKeyword]
  );

  // Khi mở modal, luôn mặc định xem danh sách trước
  useEffect(() => {
    if (open) {
      setView("list");
      loadHistory(1, statusFilter, searchKeyword);
    }
  }, [open, loadHistory, statusFilter, searchKeyword]);

  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose?.();
  };

  const handlePickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setImages((prev) => {
      const room = MAX_IMAGES - prev.length;
      if (room <= 0) {
        showErrorToast(`Tối đa ${MAX_IMAGES} ảnh.`);
        return prev;
      }

      const next = [...prev];
      for (const file of files.slice(0, room)) {
        if (!String(file.type || "").startsWith("image/")) {
          showErrorToast(`Bỏ qua «${file.name}»: không phải ảnh.`);
          continue;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          showErrorToast(`Bỏ qua «${file.name}»: vượt quá 5MB.`);
          continue;
        }
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          preview: URL.createObjectURL(file),
        });
      }
      if (files.length > room) {
        showErrorToast(`Chỉ thêm được tối đa ${MAX_IMAGES} ảnh.`);
      }
      return next;
    });

    e.target.value = "";
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const target = prev.find((x) => x.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  // Gửi yêu cầu mới
  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 10) {
      showErrorToast("Vui lòng mô tả chi tiết hơn (tối thiểu 10 ký tự).");
      return;
    }

    setSubmitting(true);
    try {
      await submitAppFeedback(
        {
          title: title.trim() || undefined,
          message: trimmed,
          category,
          priority,
          rating: rating > 0 ? rating : undefined,
          app_version: packageJson?.version || undefined,
          device_info: buildDeviceInfo(),
        },
        images.map((x) => x.file)
      );
      showSuccessToast("Đã gửi yêu cầu hỗ trợ thành công!");
      resetForm();
      // Quay về danh sách nhật ký và tải lại
      setView("list");
      loadHistory(1, "", "");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Không gửi được yêu cầu. Thử lại sau.";
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Tự cập nhật trạng thái yêu cầu
  const handleUpdateStatus = async (ticketId, newStatus) => {
    setUpdatingId(ticketId);
    try {
      await updateAppFeedbackStatus(ticketId, newStatus);
      showSuccessToast("Đã cập nhật trạng thái yêu cầu.");
      setTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t))
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Không thể cập nhật trạng thái.";
      showErrorToast(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  // Xóa yêu cầu
  const handleDeleteTicket = (ticketId) => {
    showConfirmDialog({
      title: "Xóa yêu cầu hỗ trợ",
      text: "Bạn có chắc chắn muốn xóa yêu cầu hỗ trợ này không?",
      confirmText: "Xóa",
    }).then(async (res) => {
      if (!res.isConfirmed) return;
      try {
        await deleteAppFeedback(ticketId);
        showSuccessToast("Đã xóa yêu cầu thành công.");
        setTickets((prev) => prev.filter((t) => t.id !== ticketId));
        setTotal((prev) => Math.max(0, prev - 1));
      } catch (err) {
        showErrorToast(
          err?.response?.data?.message || "Không thể xóa yêu cầu."
        );
      }
    });
  };

  if (typeof window === "undefined" || !window.document?.body) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="fb-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden
          />

          <div className="pointer-events-none fixed inset-0 z-[210] flex items-end justify-center p-0 sm:items-center sm:p-4">
            <motion.div
              key="fb-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Hub Hỗ trợ & Phản hồi"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="relative pointer-events-auto flex h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-[86vh] sm:max-h-[850px] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* ========================================================= */}
              {/* VIEW 1: DANH SÁCH NHẬT KÝ HỖ TRỢ (GIỐNG HUB THÔNG BÁO)     */}
              {/* ========================================================= */}
              {view === "list" && (
                <>
                  {/* HEADER */}
                  <div className="shrink-0 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-3.5 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-2.5 text-red-700 shadow-sm shrink-0">
                          <MessageSquarePlus className="h-5 w-5" strokeWidth={2.25} />
                        </div>
                        <div className="min-w-0">
                          <h2 className="text-base font-black uppercase tracking-tight text-gray-800 sm:text-lg">
                            Hub Hỗ trợ &amp; <span className="text-red-700">Phản hồi</span>
                          </h2>
                          <p className="text-[11px] font-bold text-gray-400 truncate">
                            Xem nhật ký và tiến độ xử lý yêu cầu hỗ trợ
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {/* NÚT GỬI YÊU CẦU TRÊN DESKTOP */}
                        <button
                          type="button"
                          onClick={() => setView("create")}
                          className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl text-xs font-bold shadow-sm shadow-red-200 hover:bg-red-700 active:scale-95 transition"
                        >
                          <Plus size={15} strokeWidth={2.5} />
                          <span>Gửi yêu cầu</span>
                        </button>

                        <button
                          type="button"
                          onClick={handleClose}
                          className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                          aria-label="Đóng"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* BỘ LỌC & TÌM KIẾM */}
                  <div className="shrink-0 border-b border-gray-100 bg-white p-3 sm:px-6 sm:py-3 space-y-2.5">
                    {isSuperadmin && (
                      <div className="rounded-xl border border-red-200 bg-red-50/70 px-3 py-1.5 text-xs text-red-800 font-medium flex items-center gap-2">
                        <ShieldAlert size={15} className="text-red-600 shrink-0" />
                        <span>
                          Chế độ <strong>Superadmin</strong>: Bạn đang xem tất cả yêu cầu trong hệ thống.
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search
                          size={15}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="text"
                          value={searchKeyword}
                          onChange={(e) => setSearchKeyword(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") loadHistory(1, statusFilter, e.target.value);
                          }}
                          placeholder="Tìm theo tiêu đề, nội dung..."
                          className="w-full rounded-xl border border-gray-200 bg-gray-50/60 pl-9 pr-3 py-2 text-xs font-medium text-gray-700 outline-none focus:border-red-500 focus:bg-white transition"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => loadHistory(1, statusFilter, searchKeyword)}
                        disabled={loadingHistory}
                        className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 hover:text-red-600 hover:border-red-200 transition shrink-0"
                        title="Làm mới"
                      >
                        <RotateCw
                          size={15}
                          className={loadingHistory ? "animate-spin" : ""}
                        />
                      </button>
                    </div>

                    {/* STATUS FILTER PILLS */}
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mr-1">
                        Trạng thái:
                      </span>
                      {[
                        { value: "", label: "Tất cả" },
                        { value: "pending", label: "Đang chờ xử lý" },
                        { value: "in_progress", label: "Đã tiếp nhận" },
                        { value: "resolved", label: "Đã xử lý" },
                      ].map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => {
                            setStatusFilter(s.value);
                            loadHistory(1, s.value, searchKeyword);
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                            statusFilter === s.value
                              ? "bg-red-600 text-white shadow-sm"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* DANH SÁCH YÊU CẦU */}
                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50/60 p-3 sm:p-5 space-y-3 pb-24 sm:pb-5">
                    {loadingHistory ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <RotateCw size={28} className="animate-spin text-red-600" />
                        <span className="mt-2 text-xs font-medium">Đang tải nhật ký…</span>
                      </div>
                    ) : tickets.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400 px-4">
                        <MessageSquarePlus size={36} className="text-gray-300 mb-2" />
                        <p className="text-sm font-semibold text-gray-600">
                          Chưa có yêu cầu hỗ trợ nào
                        </p>
                        <p className="text-xs text-gray-400 mt-1 max-w-xs">
                          {statusFilter || searchKeyword
                            ? "Không tìm thấy yêu cầu khớp với bộ lọc hiện tại."
                            : "Khi bạn gửi yêu cầu hỗ trợ, toàn bộ tiến độ sẽ được theo dõi tại đây."}
                        </p>
                        <button
                          type="button"
                          onClick={() => setView("create")}
                          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bold shadow-sm shadow-red-200 active:scale-95 transition"
                        >
                          <Plus size={15} strokeWidth={2.5} />
                          <span>Gửi yêu cầu ngay</span>
                        </button>
                      </div>
                    ) : (
                      tickets.map((t) => {
                        const statusObj = STATUS_CONFIG[t.status] || STATUS_CONFIG.pending;
                        const catBadge = CATEGORY_BADGES[t.category] || "bg-gray-100 text-gray-700";
                        const priBadge = PRIORITY_BADGES[t.priority] || "text-gray-600";
                        const attachments = Array.isArray(t.attachments) ? t.attachments : [];

                        return (
                          <div
                            key={t.id}
                            className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition space-y-2.5"
                          >
                            {/* DÒNG 1: BADGES + TRẠNG THÁI DROPDOWN */}
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded-md border uppercase ${catBadge}`}
                                >
                                  {CATEGORIES.find((c) => c.value === t.category)?.label || t.category}
                                </span>
                                <span className={`text-xs ${priBadge}`}>
                                  Ưu tiên: {PRIORITIES.find((p) => p.value === t.priority)?.label || t.priority}
                                </span>
                                <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                  <Clock size={11} className="text-gray-400" />
                                  {formatDateTimeVN(t.created_at)}
                                </span>
                              </div>

                              {/* DROPDOWN TỰ CẬP NHẬT TRẠNG THÁI */}
                              <div className="relative shrink-0">
                                <select
                                  value={t.status}
                                  onChange={(e) => handleUpdateStatus(t.id, e.target.value)}
                                  disabled={updatingId === t.id}
                                  className={`appearance-none text-xs font-bold rounded-xl border px-3 py-1.5 pr-7 outline-none cursor-pointer transition ${statusObj.badge} hover:brightness-95 disabled:opacity-50`}
                                  title="Nhấn để tự cập nhật trạng thái yêu cầu của bạn"
                                >
                                  <option value="pending">⏳ Đang chờ xử lý</option>
                                  <option value="in_progress">🔄 Đã tiếp nhận</option>
                                  <option value="resolved">✅ Đã xử lý</option>
                                </select>
                                <ChevronDown
                                  size={13}
                                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-current opacity-70"
                                />
                              </div>
                            </div>

                            {/* DÒNG 2: THÔNG TIN NGƯỜI GỬI (NẾU LÀ SUPERADMIN) */}
                            {isSuperadmin && t.user_name && (
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-100">
                                <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
                                  <User size={12} className="text-red-500" />
                                  {t.user_name}
                                </span>
                                {t.user_email && (
                                  <span className="inline-flex items-center gap-1">
                                    <Mail size={12} className="text-gray-400" />
                                    {t.user_email}
                                  </span>
                                )}
                                {t.user_phone && (
                                  <span className="inline-flex items-center gap-1">
                                    <Phone size={12} className="text-gray-400" />
                                    {t.user_phone}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* DÒNG 3: TIÊU ĐỀ & NỘI DUNG */}
                            <div>
                              <h4 className="text-sm font-bold text-gray-800 leading-snug">
                                {t.title}
                              </h4>
                              <p className="mt-1 text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
                                {t.message}
                              </p>
                            </div>

                            {/* DÒNG 4: ẢNH ĐÍNH KÈM (THUMBNAIL LIGHTBOX) */}
                            {attachments.length > 0 && (
                              <div className="pt-1">
                                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                  Ảnh đính kèm ({attachments.length}):
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {attachments.map((imgUrl, i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => setPreviewImage(imgUrl)}
                                      className="relative h-14 w-14 rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:scale-105 transition shrink-0 group"
                                      title="Nhấn để phóng to ảnh"
                                    >
                                      <img
                                        src={imgUrl}
                                        alt={`Ảnh đính kèm ${i + 1}`}
                                        className="h-full w-full object-cover"
                                      />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* DÒNG 5: RATING & NÚT XÓA */}
                            <div className="flex items-center justify-between pt-1 border-t border-gray-50 text-[11px] text-gray-400">
                              <div className="flex items-center gap-2">
                                {t.rating > 0 && (
                                  <div className="inline-flex items-center gap-0.5 text-amber-500">
                                    {[...Array(t.rating)].map((_, idx) => (
                                      <Star
                                        key={idx}
                                        size={12}
                                        className="fill-amber-400 text-amber-400"
                                      />
                                    ))}
                                  </div>
                                )}
                                {t.app_version && (
                                  <span>v{t.app_version}</span>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={() => handleDeleteTicket(t.id)}
                                className="inline-flex items-center gap-1 text-gray-400 hover:text-red-600 transition p-1"
                                title="Xóa yêu cầu này"
                              >
                                <Trash2 size={13} />
                                <span>Xóa</span>
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* PHÂN TRANG NHẬT KÝ */}
                  {totalPages > 1 && (
                    <div className="shrink-0 flex items-center justify-between border-t border-gray-100 bg-white px-5 py-2.5 text-xs text-gray-600">
                      <span>
                        Trang <strong>{page}</strong> / {totalPages} ({total} yêu cầu)
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          disabled={page <= 1}
                          onClick={() => loadHistory(page - 1, statusFilter, searchKeyword)}
                          className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white font-medium disabled:opacity-40"
                        >
                          Trước
                        </button>
                        <button
                          type="button"
                          disabled={page >= totalPages}
                          onClick={() => loadHistory(page + 1, statusFilter, searchKeyword)}
                          className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white font-medium disabled:opacity-40"
                        >
                          Sau
                        </button>
                      </div>
                    </div>
                  )}

                  {/* NÚT GỬI YÊU CẦU TRÊN MOBILE — NẰM Ở GÓC DƯỚI BÊN PHẢI (FLOATING ACTION BUTTON) */}
                  <button
                    type="button"
                    onClick={() => setView("create")}
                    className="sm:hidden absolute bottom-5 right-5 z-30 inline-flex items-center gap-1.5 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full font-bold text-xs shadow-xl shadow-red-500/40 active:scale-95 transition-all"
                    aria-label="Gửi yêu cầu hỗ trợ"
                  >
                    <Plus size={17} strokeWidth={2.5} />
                    <span>Gửi yêu cầu</span>
                  </button>
                </>
              )}

              {/* ========================================================= */}
              {/* VIEW 2: FORM GỬI YÊU CẦU HỖ TRỢ                           */}
              {/* ========================================================= */}
              {view === "create" && (
                <form
                  onSubmit={handleSubmit}
                  className="flex min-h-0 flex-1 flex-col justify-between"
                >
                  {/* HEADER VIEW TẠO */}
                  <div className="shrink-0 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-3.5 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          onClick={() => setView("list")}
                          disabled={submitting}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl border border-gray-200 bg-white text-gray-600 hover:text-red-600 hover:border-red-200 text-xs font-bold transition shrink-0"
                          title="Quay lại danh sách"
                        >
                          <ArrowLeft size={15} />
                          <span>Danh sách</span>
                        </button>
                        <div className="min-w-0">
                          <h2 className="text-base font-black uppercase tracking-tight text-gray-800 truncate">
                            Gửi yêu cầu <span className="text-red-700">hỗ trợ</span>
                          </h2>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={submitting}
                        className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 shrink-0"
                        aria-label="Đóng"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>

                  {/* FORM BODY */}
                  <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-slate-50/40 px-5 py-4 sm:px-6 sm:py-5">
                    <p className="text-xs text-gray-500">
                      Thông tin tài khoản của bạn sẽ được gửi kèm để đội ngũ kỹ thuật liên hệ lại.
                      Bạn có thể đính kèm ảnh chụp màn hình để mô tả chi tiết.
                    </p>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <FieldLabel>Phân loại yêu cầu</FieldLabel>
                        <select
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          className={`${inputClass} cursor-pointer`}
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel>Mức ưu tiên</FieldLabel>
                        <select
                          value={priority}
                          onChange={(e) => setPriority(e.target.value)}
                          className={`${inputClass} cursor-pointer`}
                        >
                          {PRIORITIES.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <FieldLabel>
                        Tiêu đề{" "}
                        <span className="font-normal normal-case tracking-normal text-gray-400">
                          (tuỳ chọn)
                        </span>
                      </FieldLabel>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={200}
                        placeholder="Tóm tắt ngắn gọn vấn đề cần hỗ trợ…"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <FieldLabel required>Nội dung chi tiết</FieldLabel>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        maxLength={4000}
                        required
                        placeholder="Mô tả lỗi, các bước tái hiện hoặc thông tin bạn cần giúp đỡ…"
                        className={`${inputClass} min-h-[110px] resize-y`}
                      />
                    </div>

                    <div>
                      <FieldLabel>
                        Ảnh đính kèm{" "}
                        <span className="font-normal normal-case tracking-normal text-gray-400">
                          (tối đa {MAX_IMAGES} ảnh · 5MB/ảnh)
                        </span>
                      </FieldLabel>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handlePickImages}
                      />

                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                        {images.map((item) => (
                          <div
                            key={item.id}
                            className="group relative aspect-square overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                          >
                            <img
                              src={item.preview}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(item.id)}
                              disabled={submitting}
                              className="absolute right-1 top-1 rounded-lg bg-white/95 p-1 text-gray-600 shadow-sm transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                              aria-label="Xóa ảnh"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}

                        {images.length < MAX_IMAGES ? (
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={submitting}
                            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-gray-300 bg-white text-gray-500 transition hover:border-red-300 hover:bg-red-50/50 hover:text-red-600 disabled:opacity-50"
                          >
                            <ImagePlus size={20} strokeWidth={1.75} />
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                              Thêm ảnh
                            </span>
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <FieldLabel>
                        Đánh giá trải nghiệm{" "}
                        <span className="font-normal normal-case tracking-normal text-gray-400">
                          (tuỳ chọn)
                        </span>
                      </FieldLabel>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setRating((r) => (r === n ? 0 : n))}
                            className="rounded-lg p-1 transition hover:bg-red-50"
                            aria-label={`${n} sao`}
                          >
                            <Star
                              size={20}
                              className={
                                n <= rating
                                  ? "fill-amber-400 text-amber-400"
                                  : "text-gray-300"
                              }
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* FORM FOOTER */}
                  <div className="flex shrink-0 gap-3 border-t border-gray-100 bg-white px-5 py-3.5 sm:justify-end sm:px-6">
                    <button
                      type="button"
                      onClick={() => setView("list")}
                      disabled={submitting}
                      className="flex-1 rounded-xl border border-gray-300 py-3 text-xs sm:text-sm font-bold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 sm:flex-none sm:px-5 sm:py-2.5"
                    >
                      Hủy
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-red-700 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider text-white shadow-md shadow-red-200 transition hover:bg-red-700 active:scale-[0.98] disabled:opacity-60 sm:flex-none sm:px-6 sm:py-2.5"
                    >
                      {submitting ? (
                        <>
                          <RotateCw size={16} className="animate-spin" />
                          Đang gửi…
                        </>
                      ) : (
                        "Gửi yêu cầu"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>

          {/* LIGHTBOX PHÓNG TO ẢNH */}
          {previewImage && (
            <div
              className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4"
              onClick={() => setPreviewImage(null)}
            >
              <div
                className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-2xl bg-black"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={previewImage}
                  alt="Ảnh xem trước"
                  className="max-h-[85vh] max-w-[85vw] object-contain rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="absolute top-3 right-3 rounded-full bg-white/80 p-2 text-gray-800 hover:bg-white shadow-lg transition"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      ) : null}
    </AnimatePresence>,
    window.document.body
  );
}
